import React, { useEffect, useState } from "react";
import posed, { PoseGroup } from "react-pose";
import { useInfiniteScroll } from "../../../hooks";
import { styled, media } from "../../../styles";
import { Link } from "../../../components";
import { ProjectTile } from "./ProjectTile";
import { ProjectInterface } from "../../ProjectPage";

export interface ProjectGridProps {
  selectedCategory?: string;
  searchTerm?: string;
  projects: ProjectInterface[];
  itemCount: number;
  setReachedEnd: (reachedEnd: boolean) => void;
}

export const ProjectGrid: React.SFC<ProjectGridProps> = ({
  projects,
  searchTerm,
  selectedCategory,
  itemCount,
  setReachedEnd
}) => {
  const projectsToLoadCount = itemCount;
  const [projectIdx, setProjectIdx] = useState(projectsToLoadCount);

  const loadMore = (callback: () => void) => {
    if (projects.length > 0 && projectIdx < projects.length) {
      setProjectIdx(projectIdx + projectsToLoadCount);
    }

    callback();
  };

  useInfiniteScroll(loadMore);

  useEffect(() => {
    if (projects.length > 0) {
      // reset counter on cat-change
      setProjectIdx(projectsToLoadCount);
      setReachedEnd(projectsToLoadCount >= projects.length);
    }
  }, [selectedCategory, projects.length]);

  useEffect(() => {
    if (projectIdx >= projects.length) {
      setReachedEnd(true);
    }
  }, [projectIdx, projects.length]);

  return (
    <Grid key={selectedCategory}>
      <PoseGroup animateOnMount={true}>
        {projects.slice(0, projectIdx).map(project => (
          <AnimatedProjectTile
            key={project.id}
            widthRatio={project.widthRatio}
          >
            <Link
              to={`/work/project/${project.slug}/${project.title}`}
              state={{
                selectedCategory,
                searchTerm
              }}
            >
              <ProjectTile project={project} ar={project.widthRatio} />
            </Link>
          </AnimatedProjectTile>
        ))}
      </PoseGroup>
    </Grid>
  );
};

const Grid = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: flex-start;
  width: 100vw;

  ${media.tablet} {
    li:last-of-type {
      flex-grow: 0;
    }
  }
`;

const AnimatedProjectTile = styled(
  posed.li({
    enter: {
      scale: 1,
      transition: { duration: 250, ease: "linear" }
    },
    exit: { scale: 0 }
  })
)`
  cursor: pointer;
  flex-shrink: 1;
  flex-grow: 1;
  margin: 0.2rem;
  padding-bottom: 1rem;

  height: 100vw;
  flex-basis: 100%;

  ${media.tablet} {
    flex-basis: ${({ widthRatio }) => `calc(${widthRatio} * (30vw/100))`};
    height: 40vw;
  }

  ${media.laptop} {
    height: 25vw;
    flex-basis: ${({ widthRatio }) => `calc(${widthRatio} * (20vw/100))`};
    padding-bottom: unset;
  }

  ${media.desktop} {
    height: 20vw;
    flex-basis: ${({ widthRatio }) => `calc(${widthRatio} * (20vw/100))`};
  }

  ${media.hd} {
    height: 15vw;
    flex-basis: ${({ widthRatio }) => `calc(${widthRatio} * (15vw/100))`};
  }
`;
